import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { StaticQuery, graphql } from 'gatsby';
import Helmet from 'react-helmet';
import ReactGA from 'react-ga4';

import { GlobalStyle } from '@sayido/ui';

import Nav from './Nav';

const AdditionalGlobalStyles = createGlobalStyle`
  :root {
    --body-font-family: 'bodytypeface',  system-ui, sans-serif;
    font-size: 10px;
  }

  * {
    box-sizing: border-box;
  }
  
  a {
    color: var(--primary-600);
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  body {
    font-weight: 400;
    margin: 0;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    color: rgba(0, 0, 0, 0.8);
    min-height: 100vh;
    position: relative;
    font-size: 1.6rem;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    line-height: 1.5;
    font-family: 'bodytypeface', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
      'Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
      'Segoe UI Symbol';
  }

  h2 {
    font-size: 2.5rem;
  }

  h3 {
    font-size: 2.4rem;
  }

  h4 {
    font-size: 1.6rem;
  }
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

export default function Layout({
  children,
  utms = null,
  showNav = true,
  showNavLinks = true,
  themeColor = '#fff',
}) {
  const handleAcquisitionSource = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const getUTM = name => {
      return !!utms ? utms[name] : urlParams.get(`utm_${name}`);
    };
    const affiliate = urlParams.get('ref');
    const docReferrer = document.referrer;
    const utm_source = getUTM('source');
    const utm_medium = getUTM('medium');
    const utm_campaign = getUTM('campaign');
    const utm_subcampaign = getUTM('subcampaign');
    const utm_landing = getUTM('landing');
    const hasAnyUTM = !!utm_source || !!utm_campaign || !!utm_medium;
    const hasExistingUTMs = !!localStorage.getItem('utm_source');
    const differentCampaign = localStorage.getItem('utm_campaign') !== affiliate;

    if (hasExistingUTMs && !differentCampaign) {
      return;
    }

    // If the user has come from an affiliate link, set the
    // UTMs accordingly, with the affiliate in the campaign.
    if (affiliate) {
      localStorage.setItem('utm_source', 'affiliate');
      localStorage.setItem('utm_medium', 'link');
      localStorage.setItem('utm_campaign', affiliate);
      localStorage.setItem('utm_landing', 'home');
    } else if (!!localStorage && hasAnyUTM) {
      localStorage.setItem('utm_source', utm_source);
      localStorage.setItem('utm_medium', utm_medium);
      localStorage.setItem('utm_campaign', utm_campaign);
      localStorage.setItem('utm_subcampaign', utm_subcampaign);
      localStorage.setItem('utm_landing', utm_landing);
      console.log('UTM Params Stashed');
    } else if (docReferrer) {
      // As a last ditch effort, try to get the referrer and set that as the campaign
      const { hostname } = new URL(docReferrer);
      localStorage.setItem('utm_source', 'referrer');
      localStorage.setItem('utm_medium', 'link');
      localStorage.setItem('utm_campaign', hostname);
      localStorage.setItem('utm_landing', document.location.pathname);
    }
  };

  useEffect(() => {
    handleAcquisitionSource();
    // Initialise GA4
    ReactGA.initialize([
      { trackingId: 'G-Z21JH4918D' },
      { trackingId: 'AW-772647180' },
    ]);
    ReactGA.send('pageview');
  });

  return (
    <StaticQuery
      query={graphql`
        query SiteTitleQuery {
          site {
            siteMetadata {
              title
            }
          }
        }
      `}
      render={data => (
        <>
          <Helmet>
            <meta charSet="utf-8" />
            {/* <title>{data.site.siteMetadata.title}</title> */}
            <meta
              name="description"
              content="Say I do wedding planning tools and websites"
            />
            <meta name="theme-color" content={themeColor} />
            <link rel="shortcut icon" href="/favicon.svg" />
            <link
              rel="mask-icon"
              href="/icons/safari-pinned-tab.svg"
              color="#DF9D8D"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_13_mini__iPhone_12_mini__iPhone_11_Pro__iPhone_XS__iPhone_X_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/iPhone_8__iPhone_7__iPhone_6s__iPhone_6__4.7__iPhone_SE_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/12.9__iPad_Pro_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 820px) and (device-height: 1180px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/10.9__iPad_Air_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 393px) and (device-height: 852px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_14_Pro_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_14__iPhone_13_Pro__iPhone_13__iPhone_12_Pro__iPhone_12_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_11_Pro_Max__iPhone_XS_Max_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 414px) and (device-height: 736px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_8_Plus__iPhone_7_Plus__iPhone_6s_Plus__iPhone_6_Plus_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 320px) and (device-height: 568px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/4__iPhone_SE__iPod_touch_5th_generation_and_later_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 834px) and (device-height: 1194px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/11__iPad_Pro__10.5__iPad_Pro_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/10.5__iPad_Air_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 768px) and (device-height: 1024px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/9.7__iPad_Pro__7.9__iPad_mini__9.7__iPad_Air__9.7__iPad_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_14_Plus__iPhone_13_Pro_Max__iPhone_12_Pro_Max_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/iPhone_11__iPhone_XR_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 744px) and (device-height: 1133px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/8.3__iPad_Mini_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
              href="/splash-screens/10.2__iPad_portrait.png"
            />
            <link
              rel="apple-touch-startup-image"
              media="screen and (device-width: 430px) and (device-height: 932px) and (-webkit-device-pixel-ratio: 3) and (orientation: portrait)"
              href="/splash-screens/iPhone_14_Pro_Max_portrait.png"
            />
            <meta name="apple-mobile-web-app-capable" content="yes"></meta>
            <script type="application/ld+json">{`
              {
                "@context": "http://schema.org",
                "@type": "Organization",
                "name": "Say I do",
                "url": "https://sayi.do",
                "logo": "https://sayi.do/assets/img/shared/logo.svg",
                "sameAs": [
                  "https://www.facebook.com/sayido.au",
                  "https://www.instagram.com/sayi_do/",
                  "https://www.linkedin.com/company/sayido-weddings/about/"
                ],
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "152 Elizabeth Street",
                  "addressLocality": "Melbourne",
                  "addressRegion": "Victoria",
                  "postalCode": "3000",
                  "addressCountry": "AU"
                }
              }
            `}</script>
          </Helmet>
          {showNav && <Nav showNavLinks={showNavLinks} />}
          <Content>{children}</Content>
          <GlobalStyle />
          <AdditionalGlobalStyles />
        </>
      )}
    />
  );
}
